import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GoogleMap, OverlayView, useLoadScript } from '@react-google-maps/api';
import { fleetService } from '../services/fleetService';
import {
  Search, MapPin, Car, User, ChevronLeft, ChevronRight,
  RefreshCw, Navigation, WifiOff, Layers, Phone, Copy,
} from 'lucide-react';
import { cn } from '../utils/cn';

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyAtQ2U-p3mBUebXuaZdC-oqsk3AldtJhE8';

const DRIVER_PALETTE = [
  '#6366F1', '#EC4899', '#F59E0B', '#10B981', '#3B82F6',
  '#EF4444', '#8B5CF6', '#14B8A6', '#F97316', '#84CC16',
  '#06B6D4', '#A855F7', '#E11D48', '#0EA5E9', '#22C55E',
];

// ── Online  = online, no active ride
// ── On Ride = currently transporting a customer
// ── Offline = not online
const STATUS_CONFIG = {
  'on-ride': { color: '#F97316', label: 'On Ride', bg: '#FFF7ED' },
  online:    { color: '#22C55E', label: 'Online',  bg: '#F0FDF4' },
  offline:   { color: '#9CA3AF', label: 'Offline', bg: '#F9FAFB' },
};

const RIDE_STATUS_LABELS = {
  accepted:    'Driver accepted ride',
  arrived:     'Driver arrived at pickup',
  picked_up:   'Customer picked up',
  in_progress: 'Ride in progress',
};

const MAP_STYLES = [
  { featureType: 'poi',           elementType: 'labels',      stylers: [{ visibility: 'off' }] },
  { featureType: 'transit',       elementType: 'labels',      stylers: [{ visibility: 'off' }] },
  { featureType: 'road',          elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape',     elementType: 'geometry',    stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'water',         elementType: 'geometry',    stylers: [{ color: '#c9d6e3' }] },
  { featureType: 'road',          elementType: 'geometry',    stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'geometry',    stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'road.highway',  elementType: 'geometry',    stylers: [{ color: '#e8e8e8' }] },
  { featureType: 'road.highway',  elementType: 'geometry.stroke', stylers: [{ color: '#d6d6d6' }] },
];

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getDriverColor(driverId) {
  const s = String(driverId);
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return DRIVER_PALETTE[Math.abs(hash) % DRIVER_PALETTE.length];
}

// Handles PostGIS {x,y}, string "(lng,lat)", and {lat,lng} / {latitude,longitude}
function parseCoords(value) {
  if (!value) return null;
  let lat, lng;
  if (typeof value === 'object' && !Array.isArray(value)) {
    if (value.x !== undefined)        { lng = value.x;         lat = value.y;         }
    else if (value.lat !== undefined)  { lat = value.lat;       lng = value.lng;       }
    else if (value.latitude !== undefined) { lat = value.latitude; lng = value.longitude; }
  } else if (typeof value === 'string') {
    const m = value.match(/\(([^,]+),\s*([^)]+)\)/);
    if (m) { lng = parseFloat(m[1]); lat = parseFloat(m[2]); }
  }
  if (lat == null || lng == null || isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
}

function parseLocation(driver) {
  return parseCoords(driver.current_location);
}

// ─── Map Markers ──────────────────────────────────────────────────────────────

function DriverMarker({ position, driver, driverColor, statusColor, isSelected, onClick }) {
  const initials =
    `${driver.first_name?.charAt(0) || ''}${driver.last_name?.charAt(0) || ''}`.toUpperCase() || '?';
  const isActive = driver.status !== 'offline';
  const hasPhoto = !!driver.profile_image_url;

  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h / 2 })}
    >
      <div
        onClick={onClick}
        style={{ width: 60, height: 60, position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {/* Pulse */}
        {isActive && (
          <div
            className="driver-pulse-ring"
            style={{ position: 'absolute', width: 58, height: 58, borderRadius: '50%', backgroundColor: statusColor + '28', border: `2px solid ${statusColor}40` }}
          />
        )}
        {/* Selection halo */}
        {isSelected && (
          <div style={{ position: 'absolute', width: 62, height: 62, borderRadius: '50%', border: `3px solid ${statusColor}`, backgroundColor: statusColor + '18' }} />
        )}
        {/* Avatar circle */}
        <div
          style={{
            position: 'relative', width: 42, height: 42, borderRadius: '50%',
            border: `3px solid ${statusColor}`,
            overflow: 'hidden',
            backgroundColor: hasPhoto ? 'transparent' : driverColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: 'system-ui, sans-serif',
            boxShadow: `0 2px 12px ${driverColor}60, 0 1px 4px rgba(0,0,0,0.25)`,
            transform: isSelected ? 'scale(1.18)' : 'scale(1)',
            transition: 'transform 0.2s ease',
            zIndex: isSelected ? 20 : 10,
            userSelect: 'none',
          }}
        >
          {hasPhoto
            ? <img src={driver.profile_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : initials
          }
        </div>
        {/* Status dot */}
        <div style={{ position: 'absolute', bottom: 9, right: 9, width: 12, height: 12, borderRadius: '50%', backgroundColor: statusColor, border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
      </div>
    </OverlayView>
  );
}

// Pickup / Drop-off pins shown when a driver on a ride is selected
function RideMarker({ position, type }) {
  const isPickup = type === 'pickup';
  const bg    = isPickup ? '#22C55E' : '#111827';
  const label = isPickup ? 'Pickup' : 'Drop-off';

  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h })}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
        <div style={{
          backgroundColor: bg, color: '#fff',
          fontSize: 11, fontWeight: 700, fontFamily: 'system-ui, sans-serif',
          padding: '3px 10px', borderRadius: 8, marginBottom: 3,
          whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          letterSpacing: '0.02em',
        }}>
          {label}
        </div>
        {/* Stem */}
        <div style={{ width: 2, height: 8, backgroundColor: bg, opacity: 0.7 }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: bg, border: '2px solid white', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
      </div>
    </OverlayView>
  );
}

// ─── Sidebar Components ───────────────────────────────────────────────────────

function HeaderBadge({ color, count, label }) {
  return (
    <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-1" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
      <span className="text-white text-xs font-bold">{count}</span>
      <span className="text-white/70 text-xs">{label}</span>
    </div>
  );
}

function DriverListItem({ driver, isSelected, hasLocation, onClick }) {
  const cfg = STATUS_CONFIG[driver.status];
  const initials = `${driver.first_name?.charAt(0) || ''}${driver.last_name?.charAt(0) || ''}`.toUpperCase() || '?';
  const licensePlate = driver.vehicle?.license_plate || driver.vehicle?.licensePlate;
  const vehicleName  = driver.vehicle?.make ? `${driver.vehicle.make} ${driver.vehicle.model || ''}`.trim() : null;
  const hasPhoto = !!driver.profile_image_url;

  return (
    <div
      onClick={onClick}
      className={cn(
        'px-4 py-3 border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-all duration-150 flex items-center gap-3 group',
        isSelected
          ? 'bg-primary/8 dark:bg-primary-dark/15 border-l-4 border-l-primary pl-3'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800/60 border-l-4 border-l-transparent',
      )}
    >
      <div className="relative flex-shrink-0">
        <div
          className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center text-white font-bold text-sm shadow-sm"
          style={{ border: `2.5px solid ${cfg.color}`, backgroundColor: hasPhoto ? 'transparent' : driver.color }}
        >
          {hasPhoto
            ? <img src={driver.profile_image_url} alt="" className="w-full h-full object-cover" />
            : initials
          }
        </div>
        {driver.status !== 'offline' && (
          <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-gray-900" style={{ backgroundColor: cfg.color }} />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1.5">
          <span className="font-semibold text-gray-900 dark:text-white text-sm truncate leading-tight">
            {driver.first_name} {driver.last_name}
          </span>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.color + '20', color: cfg.color }}>
            {cfg.label}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {licensePlate && (
            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
              {licensePlate}
            </span>
          )}
          {vehicleName && <span className="text-xs text-gray-400 dark:text-gray-500 truncate">{vehicleName}</span>}
        </div>
        {!hasLocation && (
          <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-400">
            <WifiOff className="w-3 h-3" /><span>No location</span>
          </div>
        )}
      </div>

      {hasLocation && (
        <Navigation className={cn('w-4 h-4 flex-shrink-0 transition-opacity', isSelected ? 'opacity-100 text-primary' : 'opacity-0 group-hover:opacity-60 text-gray-400')} />
      )}
    </div>
  );
}

// Full driver detail panel — replaces the list when a driver is selected
function DriverDetailPanel({ driver, ride, onBack, onFocusMap }) {
  const cfg = STATUS_CONFIG[driver.status];
  const initials   = `${driver.first_name?.charAt(0) || ''}${driver.last_name?.charAt(0) || ''}`.toUpperCase() || '?';
  const hasPhoto   = !!driver.profile_image_url;
  const licensePlate = driver.vehicle?.license_plate || driver.vehicle?.licensePlate;
  const vehicleDesc  = driver.vehicle
    ? [driver.vehicle.year, driver.vehicle.make, driver.vehicle.model, driver.vehicle.color].filter(Boolean).join(' ')
    : null;
  const locationText = driver.location
    ? `${driver.location.lat.toFixed(6)}, ${driver.location.lng.toFixed(6)}`
    : null;

  const copyToClipboard = (text) => {
    if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* ── Back header ── */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 flex-shrink-0 sticky top-0 bg-white dark:bg-gray-950 z-10">
        <button
          onClick={onBack}
          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>
        <span className="text-sm font-semibold text-gray-900 dark:text-white">Driver Details</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* ── Driver identity ── */}
        <div className="px-4 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <div
                className="w-14 h-14 rounded-full overflow-hidden flex items-center justify-center text-white font-bold text-xl shadow-md"
                style={{ border: `3px solid ${cfg.color}`, backgroundColor: hasPhoto ? 'transparent' : driver.color }}
              >
                {hasPhoto
                  ? <img src={driver.profile_image_url} alt="" className="w-full h-full object-cover" />
                  : initials
                }
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white dark:border-gray-950" style={{ backgroundColor: cfg.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-900 dark:text-white text-base truncate">
                  {driver.first_name} {driver.last_name}
                </h3>
                {driver.location && (
                  <button onClick={onFocusMap} title="Show on map" className="flex-shrink-0 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                    <Navigation className="w-3.5 h-3.5 text-gray-400 hover:text-primary" />
                  </button>
                )}
              </div>
              <span
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full mt-1"
                style={{ backgroundColor: cfg.color + '20', color: cfg.color }}
              >
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cfg.color }} />
                {cfg.label}
              </span>
            </div>
          </div>
        </div>

        {/* ── Info rows ── */}
        <div className="divide-y divide-gray-100 dark:divide-gray-800">

          {/* Vehicle */}
          {(licensePlate || vehicleDesc) && (
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-1.5">Vehicle</p>
              <div className="flex items-start gap-2.5">
                <Car className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  {licensePlate && <p className="text-sm font-bold text-gray-800 dark:text-gray-200 font-mono tracking-wide">{licensePlate}</p>}
                  {vehicleDesc  && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{vehicleDesc}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Location */}
          {locationText && (
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-1.5">Location</p>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <p className="text-sm text-gray-700 dark:text-gray-300 font-mono">{locationText}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(locationText)}
                  title="Copy coordinates"
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
                >
                  <Copy className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
            </div>
          )}

          {/* Phone */}
          {driver.phone && (
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-1.5">Phone number</p>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <p className="text-sm text-gray-700 dark:text-gray-300">{driver.phone}</p>
                </div>
                <a
                  href={`tel:${driver.phone}`}
                  className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center transition-colors flex-shrink-0"
                  title="Call driver"
                >
                  <Phone className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
                </a>
              </div>
            </div>
          )}
        </div>

        {/* ── Ride details (only when on-ride) ── */}
        {ride && (
          <div className="px-4 pt-4 pb-6">
            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Ride details
            </p>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-800 border border-gray-100 dark:border-gray-800">

              {/* Status */}
              <div className="px-4 py-3">
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Status</p>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_CONFIG['on-ride'].color }} />
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {RIDE_STATUS_LABELS[ride.status] || ride.status}
                  </p>
                </div>
              </div>

              {/* Pickup */}
              {ride.pickup_address && (
                <div className="px-4 py-3">
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center mt-1 flex-shrink-0">
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Pickup</p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">{ride.pickup_address}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Destination */}
              {ride.destination_address && (
                <div className="px-4 py-3">
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center mt-1 flex-shrink-0">
                      <div className="w-2.5 h-2.5 rounded-sm bg-gray-800 dark:bg-gray-300" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Destination</p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">{ride.destination_address}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer */}
              {ride.customer && (
                <div className="px-4 py-3">
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Customer</p>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {ride.customer.first_name} {ride.customer.last_name}
                  </p>
                  {ride.customer.phone && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{ride.customer.phone}</p>
                  )}
                </div>
              )}

              {/* Fare */}
              {(ride.estimated_fare || ride.actual_fare) && (
                <div className="px-4 py-3">
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Fare</p>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {ride.actual_fare
                      ? `${Number(ride.actual_fare).toFixed(2)} NOK`
                      : `~${Number(ride.estimated_fare).toFixed(2)} NOK (estimated)`
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LiveMap() {
  const [searchQuery, setSearchQuery]       = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [mapCenter, setMapCenter]           = useState(null);
  const [mapZoom, setMapZoom]               = useState(12);
  const [sidebarOpen, setSidebarOpen]       = useState(true);
  const [secondsAgo, setSecondsAgo]         = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt]   = useState(null);
  const mapRef          = useRef(null);
  const hasAutocentered = useRef(false);
  const userLocation    = useRef(null);

  // Browser geolocation for initial map center
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        userLocation.current = loc;
        if (!hasAutocentered.current) setMapCenter(loc);
      },
      () => {},
      { timeout: 8000, maximumAge: 60000 },
    );
  }, []);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
    id: 'google-map-script',
  });

  const {
    data: driversData,
    isLoading,
    isError: isDriversError,
    error: driversError,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['fleet-drivers-live'],
    queryFn: () => fleetService.getDrivers({ limit: 100 }).then((r) => r.data),
    refetchInterval: 10000,
    retry: 2,
  });

  const { data: activeRidesData } = useQuery({
    queryKey: ['fleet-active-rides'],
    queryFn: () => fleetService.getActiveRides().then((r) => r.data),
    refetchInterval: 10000,
    retry: 2,
  });

  useEffect(() => { if (dataUpdatedAt) setLastUpdatedAt(dataUpdatedAt); }, [dataUpdatedAt]);

  useEffect(() => {
    const t = setInterval(() => {
      if (lastUpdatedAt) setSecondsAgo(Math.floor((Date.now() - lastUpdatedAt) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [lastUpdatedAt]);

  const rawDrivers  = useMemo(() => driversData?.data   || [], [driversData]);
  const activeRides = useMemo(() => activeRidesData?.data || [], [activeRidesData]);

  // driver_id → ride object (supports flat driver_id and nested driver.id)
  const activeRideByDriver = useMemo(() => {
    const map = {};
    activeRides.forEach((r) => {
      const id = r.driver_id ?? r.driver?.id;
      if (id) map[id] = r;
    });
    return map;
  }, [activeRides]);

  const onRideSet = useMemo(() => new Set(Object.keys(activeRideByDriver)), [activeRideByDriver]);

  const getStatus = useCallback(
    (driver) => {
      if (onRideSet.has(driver.id)) return 'on-ride';
      if (driver.is_online)         return 'online';
      return 'offline';
    },
    [onRideSet],
  );

  const allDrivers = useMemo(() =>
    rawDrivers.map((d) => ({
      ...d,
      location: parseLocation(d),
      status:   getStatus(d),
      color:    getDriverColor(d.id),
    })),
    [rawDrivers, getStatus],
  );

  const filteredDrivers = useMemo(() =>
    allDrivers.filter((d) => {
      if (selectedStatus !== 'all' && d.status !== selectedStatus) return false;
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      const name = `${d.first_name || ''} ${d.last_name || ''}`.toLowerCase();
      return (
        name.includes(q) ||
        (d.email || '').toLowerCase().includes(q) ||
        (d.phone || '').toLowerCase().includes(q) ||
        (d.vehicle?.license_plate || d.vehicle?.licensePlate || '').toLowerCase().includes(q)
      );
    }),
    [allDrivers, selectedStatus, searchQuery],
  );

  const driversOnMap = useMemo(() => filteredDrivers.filter((d) => d.location), [filteredDrivers]);

  // Auto-center to driver centroid once (driver positions beat browser geolocation)
  useEffect(() => {
    if (driversOnMap.length > 0 && !hasAutocentered.current) {
      const avgLat = driversOnMap.reduce((s, d) => s + d.location.lat, 0) / driversOnMap.length;
      const avgLng = driversOnMap.reduce((s, d) => s + d.location.lng, 0) / driversOnMap.length;
      setMapCenter({ lat: avgLat, lng: avgLng });
      hasAutocentered.current = true;
    }
  }, [driversOnMap]);

  const handleSelectDriver = useCallback((driver) => {
    const isSame = selectedDriver?.id === driver.id;
    setSelectedDriver(isSame ? null : driver);
    if (!isSame && driver.location && mapRef.current) {
      mapRef.current.panTo(driver.location);
      mapRef.current.setZoom(15);
    }
  }, [selectedDriver]);

  const handleFocusSelectedOnMap = useCallback(() => {
    if (selectedDriver?.location && mapRef.current) {
      mapRef.current.panTo(selectedDriver.location);
      mapRef.current.setZoom(16);
    }
  }, [selectedDriver]);

  const counts = useMemo(() => ({
    online:  allDrivers.filter((d) => d.status === 'online').length,
    onRide:  allDrivers.filter((d) => d.status === 'on-ride').length,
    offline: allDrivers.filter((d) => d.status === 'offline').length,
  }), [allDrivers]);

  // Ride markers for the selected on-ride driver
  const selectedRide      = selectedDriver ? activeRideByDriver[selectedDriver.id] : null;
  const pickupCoords      = selectedRide ? parseCoords(selectedRide.pickup_coordinates) : null;
  const destinationCoords = selectedRide ? parseCoords(selectedRide.destination_coordinates) : null;

  const updatedLabel = secondsAgo < 5 ? 'Just now' : `${secondsAgo}s ago`;

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">Error loading Google Maps: {loadError.message}</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes driverPulse {
          0%   { transform: scale(1);   opacity: 0.6; }
          70%  { transform: scale(1.9); opacity: 0; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        .driver-pulse-ring { animation: driverPulse 2.2s ease-out infinite; }
      `}</style>

      <div className="relative w-full" style={{ height: 'calc(100vh - 4.5rem)' }}>

        {/* ── Full-screen Google Map ── */}
        <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-xl border border-gray-200 dark:border-gray-700">
          {!isLoaded ? (
            <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-gray-500 dark:text-gray-400">Loading map…</span>
              </div>
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={MAP_CONTAINER_STYLE}
              center={mapCenter ?? { lat: 30.3753, lng: 69.3451 }}
              zoom={mapZoom}
              onLoad={(map) => { mapRef.current = map; }}
              onZoomChanged={() => { if (mapRef.current) setMapZoom(mapRef.current.getZoom()); }}
              options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                clickableIcons: false,
                styles: MAP_STYLES,
              }}
            >
              {/* Driver markers */}
              {driversOnMap.map((driver) => (
                <DriverMarker
                  key={driver.id}
                  position={driver.location}
                  driver={driver}
                  driverColor={driver.color}
                  statusColor={STATUS_CONFIG[driver.status].color}
                  isSelected={selectedDriver?.id === driver.id}
                  onClick={() => handleSelectDriver(driver)}
                />
              ))}

              {/* Pickup marker for selected on-ride driver */}
              {pickupCoords && <RideMarker position={pickupCoords} type="pickup" />}

              {/* Destination marker for selected on-ride driver */}
              {destinationCoords && <RideMarker position={destinationCoords} type="destination" />}
            </GoogleMap>
          )}
        </div>

        {/* ── Floating Sidebar ── */}
        <div className={cn('absolute top-3 left-3 bottom-3 flex flex-col z-20 transition-all duration-300 ease-in-out', sidebarOpen ? 'w-72' : 'w-auto')}>
          {sidebarOpen ? (
            <div className="flex-1 flex flex-col rounded-2xl shadow-2xl overflow-hidden bg-white/96 dark:bg-gray-950/96 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60">

              {/* ── Header (always visible) ── */}
              <div className="px-4 pt-4 pb-3 bg-gradient-to-br from-primary via-primary to-primary-dark flex-shrink-0">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                      <Layers className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-white font-bold text-sm leading-tight">Live Tracking</h2>
                      <p className="text-white/60 text-xs">{allDrivers.length} drivers total</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setSidebarOpen(false); setSelectedDriver(null); }}
                    className="w-7 h-7 bg-white/15 hover:bg-white/25 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4 text-white" />
                  </button>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  <HeaderBadge color={STATUS_CONFIG.online.color}    count={counts.online}  label="Online"  />
                  <HeaderBadge color={STATUS_CONFIG['on-ride'].color} count={counts.onRide}  label="On ride" />
                  <HeaderBadge color={STATUS_CONFIG.offline.color}    count={counts.offline} label="Offline" />
                </div>
              </div>

              {/* ── Detail panel (when driver selected) ── */}
              {selectedDriver ? (
                <DriverDetailPanel
                  driver={selectedDriver}
                  ride={selectedRide}
                  onBack={() => setSelectedDriver(null)}
                  onFocusMap={handleFocusSelectedOnMap}
                />
              ) : (
                <>
                  {/* Search */}
                  <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search driver or plate…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                      />
                    </div>
                  </div>

                  {/* Filter tabs */}
                  <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
                    <div className="flex gap-1">
                      {[
                        { key: 'all',      label: 'All',     count: allDrivers.length   },
                        { key: 'online',   label: 'Online',  count: counts.online       },
                        { key: 'on-ride',  label: 'On ride', count: counts.onRide       },
                        { key: 'offline',  label: 'Offline', count: counts.offline      },
                      ].map((tab) => (
                        <button
                          key={tab.key}
                          onClick={() => setSelectedStatus(tab.key)}
                          className={cn(
                            'flex-1 px-1.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 text-center',
                            selectedStatus === tab.key
                              ? 'bg-primary text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800',
                          )}
                        >
                          {tab.label}
                          <span className="ml-0.5 opacity-65">({tab.count})</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Driver list */}
                  <div className="flex-1 overflow-y-auto">
                    {isLoading ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : isDriversError ? (
                      <div className="flex flex-col items-center justify-center h-40 gap-3 px-4 text-center">
                        <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                          <WifiOff className="w-5 h-5 text-red-500" />
                        </div>
                        <p className="text-sm font-semibold text-red-500">Failed to load drivers</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                          {driversError?.response?.data?.message || driversError?.message || 'Check that your fleet account is set up and the server is running.'}
                        </p>
                      </div>
                    ) : filteredDrivers.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-40 gap-2 px-4 text-center">
                        <User className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                        <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                          {allDrivers.length === 0 ? 'No drivers in your fleet' : 'No drivers match this filter'}
                        </p>
                        {allDrivers.length === 0 && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                            Add drivers via the Fleet portal. They appear here once approved and online.
                          </p>
                        )}
                      </div>
                    ) : (
                      filteredDrivers.map((driver) => (
                        <DriverListItem
                          key={driver.id}
                          driver={driver}
                          isSelected={selectedDriver?.id === driver.id}
                          hasLocation={!!driver.location}
                          onClick={() => handleSelectDriver(driver)}
                        />
                      ))
                    )}
                  </div>
                </>
              )}

              {/* Footer */}
              <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/80 flex-shrink-0">
                <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                  <RefreshCw className="w-3 h-3 flex-shrink-0" />
                  <span>Updated {updatedLabel}</span>
                  <span className="ml-auto text-primary dark:text-primary-light font-medium">Auto • 10s</span>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setSidebarOpen(true)}
              className="w-12 h-12 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              title="Open driver panel"
            >
              <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          )}
        </div>

        {/* ── Legend — bottom-right ── */}
        <div className="absolute bottom-4 right-4 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md rounded-xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 px-3 py-2.5">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Legend</p>
          <div className="flex flex-col gap-1.5">
            {[
              { color: STATUS_CONFIG.online.color,    label: 'Online'  },
              { color: STATUS_CONFIG['on-ride'].color, label: 'On Ride' },
              { color: STATUS_CONFIG.offline.color,    label: 'Offline' },
            ].map(({ color, label }) => (
              <div key={label} className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <div className="w-4 h-4 rounded-full flex-shrink-0 shadow-sm" style={{ backgroundColor: color, border: '2px solid white' }} />
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* ── Drivers on map — top-right ── */}
        <div className="absolute top-4 right-4 z-20">
          <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md rounded-xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 px-3 py-2 flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 text-primary" />
            <span className="font-semibold text-gray-800 dark:text-white">{driversOnMap.length}</span>
            <span className="text-gray-400 dark:text-gray-500 text-xs">on map</span>
          </div>
        </div>

      </div>
    </>
  );
}
